import React, { useState, useRef, useEffect } from 'react';
import { T, FONT, MONO, radius, tint } from './poyais-ui/theme';
import { Card, Tile, Badge, Button, Eyebrow, Grid } from './poyais-ui/primitives';

const TEMPLATES = [
  { id:'v1',  cat:'Ürün',     label:'Ürün Tanıtım',      desc:'Ürün döndürme, zoom efekti.',        thumb:'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&q=80', prompt:'Product showcase video, slow rotation, zoom in effect, clean white background, professional lighting, commercial quality' },
  { id:'v2',  cat:'Lifestyle',label:'Lifestyle Reel',     desc:'Doğal ortam, yaşam tarzı videosu.',  thumb:'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80', prompt:'Lifestyle product video, natural environment, person using product, warm golden hour lighting, authentic feel, social media style' },
  { id:'v3',  cat:'Lüks',     label:'Premium Tanıtım',    desc:'Sinematik, premium atmosfer.',        thumb:'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600&q=80', prompt:'Luxury product cinematic video, dramatic lighting, gold tones, slow motion, premium feel, dark elegant background' },
  { id:'v4',  cat:'Promosyon',label:'Flash Kampanya',     desc:'Hızlı, dikkat çekici promosyon.',     thumb:'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&q=80', prompt:'Promotional product video, fast cuts, vibrant colors, sale atmosphere, energetic music vibe, bold text overlays' },
  { id:'v5',  cat:'Yemek',    label:'Food Showcase',      desc:'İştah açıcı, buhar efektli.',         thumb:'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80', prompt:'Food video, close up appetizing shot, steam effect, warm lighting, slow pour or cut motion, restaurant quality' },
  { id:'v6',  cat:'Moda',     label:'Fashion Reels',      desc:'Şık, hareketli moda videosu.',        thumb:'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', prompt:'Fashion product video, model wearing product, elegant movement, stylish background, magazine quality, dynamic cuts' },
  { id:'v7',  cat:'Teknoloji',label:'Tech Showcase',      desc:'Modern, minimalist tech videosu.',    thumb:'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&q=80', prompt:'Technology product video, minimalist dark background, blue accent lighting, clean lines, smooth camera movement, modern aesthetic' },
  { id:'v8',  cat:'Lüks',     label:'Parfüm & Kozmetik',  desc:'Çiçek, ışık ve estetik.',             thumb:'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600&q=80', prompt:'Cosmetic perfume product video, flowers and petals falling, soft pastel colors, dreamy slow motion, luxury feel' },
];

const ASPECTS = [
  { key:'9:16', label:'9:16', desc:'Reels/Story' },
  { key:'16:9', label:'16:9', desc:'YouTube' },
  { key:'1:1',  label:'1:1',  desc:'Feed' },
];
const DURATIONS = [{key:5,label:'5 sn'},{key:10,label:'10 sn'}];
const QUALITY   = [{key:'std',label:'Standart'},{key:'pro',label:'Pro'}];

type View = 'home' | 'create';

const Spinner:React.FC=()=>(
  <span style={{display:'inline-block',width:14,height:14,border:`2px solid ${tint(T.accent,25)}`,borderTopColor:T.accent,borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
);

const AIVideoTab:React.FC<{config?:any;creditBalance?:number;onSpend?:()=>void}>=({creditBalance:creditBalanceProp=0,onSpend})=>{
  const [view,setView]             = useState<View>('home');
  const [selectedTemplate,setST]   = useState<typeof TEMPLATES[0]|null>(null);
  const [mode,setMode]             = useState<'text'|'image'>('text');
  const [prompt,setPrompt]         = useState('');
  const [aspect,setAspect]         = useState('9:16');
  const [duration,setDur]          = useState(5);
  const [quality,setQuality]       = useState('std');
  const [imgFile,setImgFile]       = useState<File|null>(null);
  const [imgPreview,setImgPreview] = useState<string|null>(null);
  const [loading,setLoading]       = useState(false);
  const [taskId,setTaskId]         = useState<string|null>(null);
  const [status,setStatus]         = useState<string|null>(null);
  const [videoUrl,setVideoUrl]     = useState<string|null>(null);
  const [error,setError]           = useState<string|null>(null);
  const fileRef  = useRef<HTMLInputElement>(null);
  const pollRef  = useRef<any>(null);

  const token = () => localStorage.getItem('poyais_token') || '';

  const pollStatus = (tid:string) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if(attempts > 60){ clearInterval(pollRef.current); setStatus('failed'); setError('Zaman aşımı.'); setLoading(false); return; }
      try {
        const res = await fetch(`/api/v1/kreativ/video/status/${tid}?client_id=${parseInt(localStorage.getItem('poyais_active_client')||'1')}`,{headers:{'Authorization':'Bearer '+token()}});
        const data = await res.json();
        setStatus(data.status);
        if(data.status==='succeed'&&data.video_url){ clearInterval(pollRef.current); setVideoUrl(data.video_url); setLoading(false); }
        else if(data.status==='failed'){ clearInterval(pollRef.current); setError('Video üretilemedi.'); setLoading(false); }
      } catch {}
    }, 5000);
  };

  const generate = async () => {
    const finalPrompt = selectedTemplate
      ? (prompt.trim() ? `${selectedTemplate.prompt}. ${prompt}` : selectedTemplate.prompt)
      : prompt;
    if(!finalPrompt.trim()) return;
    if(mode==='image'&&!imgFile) return;
    if(creditBalanceProp < 10) {
      setError('Yetersiz kredi. Video üretimi 10 kredi gerektirir. Lütfen kredi yükleyin.');
      return;
    }

    setLoading(true); setError(null); setVideoUrl(null); setTaskId(null); setStatus('submitting');
    try {
      let res:Response;
      if(mode==='image'&&imgFile){
        const fd=new FormData();
        fd.append('prompt',finalPrompt); fd.append('duration',String(duration));
        fd.append('aspect_ratio',aspect); fd.append('mode',quality); fd.append('image',imgFile);
        res=await fetch('/api/v1/kreativ/video/generate-from-image',{method:'POST',headers:{'Authorization':'Bearer '+token()},body:fd});
      } else {
        res=await fetch('/api/v1/kreativ/video/generate',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+token()},
          body:JSON.stringify({prompt:finalPrompt,duration,aspect_ratio:aspect,mode:quality,client_id:parseInt(localStorage.getItem('poyais_active_client')||'1')})});
      }
      if(!res.ok){ const e=await res.json(); throw new Error(e.detail||`HTTP ${res.status}`); }
      const data=await res.json();
      setTaskId(data.task_id); setStatus('processing'); pollStatus(data.task_id);
    } catch(e:any){ setError(e.message||'Hata oluştu.'); setLoading(false); setStatus(null); }
  };

  const goHome = () => { setView('home'); setST(null); setPrompt(''); setVideoUrl(null); setError(null); setStatus(null); setTaskId(null); if(pollRef.current) clearInterval(pollRef.current); };
  const selectTemplate = (t:typeof TEMPLATES[0]) => { setST(t); setView('create'); setVideoUrl(null); setError(null); setMode('image'); };

  const statusLabel:Record<string,string> = { submitting:'Gönderiliyor…', processing:'Video üretiliyor… (1-3 dk)', succeed:'Tamamlandı', failed:'Başarısız' };

  // ── HOME ──────────────────────────────────────────────────────────────
  useEffect(()=>{
    const loadBalance = () => {
      const tok=localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
      if(!tok) return;
      fetch('/api/v1/credits/balance',{headers:{'Authorization':'Bearer '+tok}})
        .then(r=>r.ok?r.json():Promise.reject(r.status))
        .then(d=>{ void d; });
    };
    loadBalance();
  },[]);
  if(view==='home') return (
    <div style={{fontFamily:FONT,background:T.surface,minHeight:'100vh'}}>
      {/* Kredi göstergesi */}
      <div style={{display:'flex',justifyContent:'flex-end',padding:'12px 16px 0'}}>
        <div style={{display:'inline-flex',alignItems:'center',gap:10,background:tint(T.accent,8),border:`1px solid ${tint(T.accent,20)}`,borderRadius:radius.md,padding:'8px 16px'}}>
          <span style={{fontSize:11,color:T.accentDeep,fontFamily:FONT,fontWeight:600}}>KREDİ</span>
          <span style={{fontSize:15,fontWeight:800,color:T.accent,fontFamily:MONO,minWidth:20,textAlign:'center' as const}}>{creditBalanceProp}</span>
          <Button variant="soft" color={T.accent} onClick={()=>{ (window as any).__poyaisGoNav?.('kredi-yukle'); }}
            style={{padding:'4px 12px',fontSize:11}}>
            Kredi Yükle
          </Button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}`}</style>

      <div style={{padding:'24px 16px 20px',borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:600,margin:'0 auto',textAlign:'center' as const}}>
          <Eyebrow color={T.accent}>AI Video Üretici</Eyebrow>
          <h2 style={{fontSize:26,fontWeight:800,color:T.text,margin:'8px 0 8px',letterSpacing:'-0.02em'}}>Ne üretmek istersiniz?</h2>
          <p style={{fontSize:13,color:T.textMid,margin:'0 0 20px'}}>Şablon seçin veya sıfırdan oluşturun.</p>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <Button variant="solid" color={T.accent} onClick={()=>{setView('create');setST(null);setMode('text');}}>
              Promptla Üret
            </Button>
            <Button variant="outline" color={T.accent} onClick={()=>{setView('create');setST(null);setMode('image');}}>
              Görselden Üret
            </Button>
          </div>
        </div>
      </div>

      <div style={{padding:'20px 16px 32px'}}>
        <Grid min={200} gap={16}>
          {TEMPLATES.map(t=>(
            <Card key={t.id} pad={0} hover onClick={()=>selectTemplate(t)}
              style={{overflow:'hidden',cursor:'pointer',animation:'fadeIn 0.2s ease'}}>
              <div style={{height:160,background:'#000',overflow:'hidden',position:'relative' as const}}>
                <img src={t.thumb} alt={t.label}
                  style={{width:'100%',height:'100%',objectFit:'cover' as const,display:'block'}}/>
                <div style={{position:'absolute' as const,inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.2)'}}>
                  <div style={{width:40,height:40,borderRadius:'50%',background:'rgba(255,255,255,0.9)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,paddingLeft:3}}>▶</div>
                </div>
                <div style={{position:'absolute' as const,top:8,left:8}}>
                  <Badge color={T.accent}>{t.cat}</Badge>
                </div>
              </div>
              <div style={{padding:'12px 14px'}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:3}}>{t.label}</div>
                <div style={{fontSize:11,color:T.textMid}}>{t.desc}</div>
              </div>
            </Card>
          ))}
        </Grid>
      </div>
    </div>
  );

  // ── CREATE VIEW ───────────────────────────────────────────────────────
  return (
    <div style={{fontFamily:FONT,background:T.surface,minHeight:'100vh'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>

      <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:12}}>
        <Button variant="soft" color={T.textMid} onClick={goHome} style={{padding:'6px 14px',fontSize:12}}>← Geri</Button>
        <span style={{fontSize:15,fontWeight:700,color:T.text}}>
          {selectedTemplate ? selectedTemplate.label : mode==='image' ? 'Görselden Üret' : 'Promptla Üret'}
        </span>
      </div>

      <div style={{padding:'20px 16px',display:'flex',flexDirection:'column' as const,gap:20}}>

        {/* Sol form */}
        <div style={{display:'flex',flexDirection:'column' as const,gap:14}}>

          {/* Mod seçici — template yoksa göster */}
          {!selectedTemplate&&(
            <div style={{display:'flex',background:T.card,borderRadius:radius.md,padding:3,border:`1px solid ${tint(T.accent,12)}`}}>
              {([{key:'text',label:'Metinden'},{key:'image',label:'Görselden'}] as const).map(m=>(
                <Button key={m.key} onClick={()=>setMode(m.key)}
                  variant="ghost"
                  style={{flex:1,padding:'8px',fontSize:12,fontWeight:600,
                    background:mode===m.key?T.surface:'transparent',
                    border:`1px solid ${mode===m.key?tint(T.accent,35):'transparent'}`,
                    color:mode===m.key?T.accent:T.textMid,
                    boxShadow:mode===m.key?'0 1px 3px rgba(0,0,0,.08)':'none'}}>
                  {m.label}
                </Button>
              ))}
            </div>
          )}

          {/* Görsel yükleme */}
          {(mode==='image'||selectedTemplate)&&(
            <Card pad={16}>
              <div style={{fontSize:9,color:T.textMid,textTransform:'uppercase' as const,letterSpacing:0,marginBottom:8}}>
                {selectedTemplate?'Ürün Görseli':'Referans Görsel'}
              </div>
              <div onClick={()=>fileRef.current?.click()}
                style={{padding:'14px',border:`1.5px dashed ${imgFile?tint(T.accent,35):tint(T.accent,25)}`,
                  borderRadius:radius.sm,textAlign:'center' as const,cursor:'pointer',background:imgFile?tint(T.accent,10):T.card,transition:'all .2s'}}>
                {imgPreview?(
                  <div>
                    <img src={imgPreview} alt="" style={{maxHeight:100,borderRadius:6,display:'block',margin:'0 auto 4px'}}/>
                    <div style={{fontSize:10,color:T.textMid}}>Değiştirmek için tıkla</div>
                  </div>
                ):(
                  <div>
                    <div style={{fontSize:22,marginBottom:4}}>🖼️</div>
                    <div style={{fontSize:12,color:T.text,fontWeight:600}}>Görsel yükle</div>
                    <div style={{fontSize:10,color:T.textMid,marginTop:2}}>JPG / PNG / WebP</div>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e=>{const f=e.target.files?.[0];if(!f)return;setImgFile(f);const r=new FileReader();r.onload=ev=>setImgPreview(ev.target?.result as string);r.readAsDataURL(f);}}/>
            </Card>
          )}

          {/* Prompt */}
          <Card pad={16}>
            <div style={{fontSize:9,color:T.textMid,textTransform:'uppercase' as const,letterSpacing:0,marginBottom:8}}>
              {selectedTemplate?'Ek Açıklama (Opsiyonel)':'Video Promptu'}
            </div>
            <textarea value={prompt} onChange={e=>setPrompt(e.target.value)}
              placeholder={selectedTemplate?'Ürününüzü kısaca tanımlayın…':'Video için sahne tanımı girin…'}
              rows={4} style={{width:'100%',background:T.card,border:`1px solid ${tint(T.accent,18)}`,borderRadius:7,
                color:T.text,fontSize:12,padding:'9px 12px',resize:'vertical' as const,outline:'none',
                fontFamily:'inherit',lineHeight:1.6,boxSizing:'border-box' as const}}
              onFocus={e=>e.target.style.borderColor=tint(T.accent,50)}
              onBlur={e=>e.target.style.borderColor=tint(T.accent,18)}/>
          </Card>

          {/* Format ayarları */}
          <Tile pad={16}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <div>
                <div style={{fontSize:9,color:T.textMid,textTransform:'uppercase' as const,letterSpacing:0,marginBottom:8}}>En-Boy</div>
                <div style={{display:'flex',flexDirection:'column' as const,gap:4}}>
                  {ASPECTS.map(a=>(
                    <Button key={a.key} onClick={()=>setAspect(a.key)} variant="ghost"
                      style={{padding:'6px 10px',textAlign:'left' as const,justifyContent:'space-between',
                        background:aspect===a.key?tint(T.accent,10):T.surface,border:`1px solid ${aspect===a.key?tint(T.accent,35):tint(T.accent,18)}`}}>
                      <span style={{fontSize:11,fontWeight:700,color:aspect===a.key?T.accent:T.text}}>{a.label}</span>
                      <span style={{fontSize:10,color:T.textMid}}>{a.desc}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:9,color:T.textMid,textTransform:'uppercase' as const,letterSpacing:0,marginBottom:8}}>Süre</div>
                <div style={{display:'flex',flexDirection:'column' as const,gap:4,marginBottom:14}}>
                  {DURATIONS.map(d=>(
                    <Button key={d.key} onClick={()=>setDur(d.key)} variant="ghost"
                      style={{padding:'6px 10px',fontSize:11,fontWeight:600,
                        background:duration===d.key?tint(T.accent,10):T.surface,border:`1px solid ${duration===d.key?tint(T.accent,35):tint(T.accent,18)}`,
                        color:duration===d.key?T.accent:T.text}}>
                      {d.label}
                    </Button>
                  ))}
                </div>
                <div style={{fontSize:9,color:T.textMid,textTransform:'uppercase' as const,letterSpacing:0,marginBottom:8}}>Kalite</div>
                <div style={{display:'flex',flexDirection:'column' as const,gap:4}}>
                  {QUALITY.map(q=>(
                    <Button key={q.key} onClick={()=>setQuality(q.key)} variant="ghost"
                      style={{padding:'6px 10px',fontSize:11,fontWeight:600,
                        background:quality===q.key?tint(T.accent,10):T.surface,border:`1px solid ${quality===q.key?tint(T.accent,35):tint(T.accent,18)}`,
                        color:quality===q.key?T.accent:T.text}}>
                      {q.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </Tile>

          <Button variant="solid" color={T.accent} onClick={generate}
            disabled={loading||(mode==='text'&&!selectedTemplate&&!prompt.trim())||(mode==='image'&&!imgFile&&!selectedTemplate)}
            style={{width:'100%',padding:'12px',fontSize:13,fontWeight:800,
              background:loading?T.card:T.accent,
              border:`1px solid ${loading?tint(T.accent,15):T.accent}`,
              color:loading?T.textMid:'#fff',cursor:loading?'not-allowed':'pointer'}}>
            {loading?<><Spinner/><span>{statusLabel[status||'']||'İşleniyor…'}</span></>:<span>Video Üret</span>}
          </Button>
        </div>

        {/* Sonuç */}
        <div style={{width:'100%'}}>
          {error&&(
            <div style={{background:tint(T.red,4),border:`1px solid ${tint(T.red,20)}`,borderRadius:radius.md,padding:'14px',marginBottom:14,fontSize:12,color:T.redText}}>❌ {error}</div>
          )}
          {loading&&!videoUrl&&(
            <Card style={{padding:'64px 24px',textAlign:'center' as const}}>
              <div style={{marginBottom:16,display:'flex',justifyContent:'center'}}><Spinner/></div>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>{statusLabel[status||'processing']}</div>
              <div style={{fontSize:12,color:T.textMid}}>Kling AI işliyor, 1-3 dakika…</div>
              {taskId&&<div style={{fontSize:10,color:T.textMid,marginTop:8}}>Task: {taskId.slice(0,16)}…</div>}
            </Card>
          )}
          {videoUrl&&(
            <div style={{animation:'fadeIn 0.3s ease'}}>
              <video src={videoUrl} controls style={{width:'100%',borderRadius:12,display:'block',maxHeight:480}}/>
              <div style={{display:'flex',gap:10,marginTop:12}}>
                <a href={videoUrl} download="poyais-video.mp4" target="_blank" rel="noreferrer"
                  style={{flex:1,padding:'10px',textAlign:'center' as const,background:T.accent,border:`1px solid ${T.accent}`,borderRadius:radius.sm,color:'#fff',fontSize:12,fontWeight:700,textDecoration:'none'}}>⬇ İndir</a>
                <Button variant="soft" color={T.textMid} onClick={()=>{setVideoUrl(null);setStatus(null);setTaskId(null);setError(null);}}
                  style={{flex:1,padding:'10px',fontSize:12,fontWeight:700}}>↺ Yeni Video</Button>
              </div>
            </div>
          )}
          {!loading&&!videoUrl&&!error&&(
            <Card style={{padding:'64px 24px',textAlign:'center' as const}}>
              <div style={{fontSize:48,marginBottom:16}}>🎬</div>
              <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>
                {selectedTemplate?selectedTemplate.label:'Video Üretici'}
              </div>
              <div style={{fontSize:13,color:T.textMid,lineHeight:1.7}}>
                {selectedTemplate?'Ürün görselini yükleyin ve videoyu üretin.':'Prompt yazın veya görsel yükleyin, videoyu üretin.'}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
export default AIVideoTab;
